import Button from "components/button";
import ImageUploadPreview from "components/shared/image-upload-preview";
import { VendorInformationFormValues } from "_interfaces/vendor.interfaces";
import Icon from "components/icon";
import Input from "components/input";
import PhoneInput from "components/input/phone-input";
import Select from "components/select";
import Typography from "components/typography";
import React, { useRef, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { NamespaceKeys } from "locale/types";
import VendorAccordion from "./vendor-accordion";
import { useAppSelector } from "store";
import { toast } from "react-toastify";

interface VendorInformationAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onNext: () => void;
  form: UseFormReturn<VendorInformationFormValues>;
  t: (key: NamespaceKeys<"vendor">, options?: Record<string, unknown>) => string;
  isEdit?: boolean;
}

const VendorInformationAccordion = ({
  isOpen,
  onToggle,
  onNext,
  form,
  t,
  isEdit = false,
}: VendorInformationAccordionProps) => {
  const {
    register,
    watch,
    formState: { errors },
  } = form;

  const vendorType = watch("vendorType");
  const currentLogo = watch("vendorLogo");

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleLogoUpload = (file: File) => {
    // Validate file type and size
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      toast.error(t("error-file-invalid-format" as any, { formats: ".jpg, .jpeg, .png" } as any) || "Invalid logo format. Use JPG or PNG.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t("error-file-max-size" as any, { size: 2 } as any) || "Logo size exceeds 2MB.");
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);

    form.setValue("vendorLogo", localPreview, { shouldDirty: true });
    form.setValue("vendorLogoFile", file, { shouldDirty: true });
  };

  const handleLogoRemove = () => {
    setPreviewUrl(null);
    form.setValue("vendorLogo", "", { shouldDirty: true });
    form.setValue("vendorLogoFile", null, { shouldDirty: true });
  };

  const displayUrl = previewUrl || currentLogo;

  return (
    <VendorAccordion
      title={t("accordion-vendor-information")}
      subtitle={t("accordion-vendor-information-subtitle")}
      iconName="profile-2user"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-4">
        <div>
          <Typography variant="bodyLarge" className="mb-2">
            {t("label-vendor-type")} <span className="text-error-500">*</span>
          </Typography>
          <Controller
            name="vendorType"
            control={form.control}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-3" data-testid="select-vendor-type">
                <button
                  type="button"
                  className={`h-[38px] rounded-lg border px-3 text-left text-sm ${
                    field.value === "individual"
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-border"
                  }`}
                  onClick={() => field.onChange("individual")}
                >
                  {t("vendor-type-individual")}
                </button>
                <button
                  type="button"
                  className={`h-[38px] rounded-lg border px-3 text-left text-sm ${
                    field.value === "company"
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-border"
                  }`}
                  onClick={() => field.onChange("company")}
                >
                  {t("vendor-type-company")}
                </button>
              </div>
            )}
          />
        </div>

        <div className="grid grid-cols-[172px_1fr] gap-4">
          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">{t("label-vendor-logo")}</Typography>
            <ImageUploadPreview
              id="vendor-logo-upload"
              dataTestId="upload-vendor-logo"
              value={displayUrl}
              fileName={form.watch("vendorLogoFile")?.name}
              onUpload={handleLogoUpload}
              onRemove={handleLogoRemove}
              dropText={t("logo-drop-text")}
              helperText={t("logo-drop-helper")}
              accept="image/jpeg,image/png,image/jpg"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t("label-vendor-name")}
              required
              placeholder={t("placeholder-vendor-name")}
              data-testid="input-vendor-name"
              {...register("vendorName")}
              error={errors.vendorName}
            />
            <Input
              label={t("label-vendor-id")}
              placeholder={t("placeholder-vendor-id")}
              {...register("vendorId")}
              error={errors.vendorId}
              disabled={isEdit}
            />

            <Input
              label={t("label-npwp")}
              required
              placeholder={t("placeholder-npwp")}
              data-testid="input-npwp"
              {...register("npwp")}
              error={errors.npwp}
            />
            <Input
              label={t("label-vendor-email")}
              required
              placeholder={t("placeholder-vendor-email")}
              {...register("email")}
              error={errors.email}
            />

            <Input
              label={t("label-contact-person-name")}
              required
              placeholder={t("placeholder-contact-person-name")}
              {...register("contactPersonName")}
              error={errors.contactPersonName}
            />
            <Controller
              name="contactPersonPhoneNumber"
              control={form.control}
              render={({ field, fieldState }) => (
                <PhoneInput
                  label={t("label-contact-person-phone")}
                  required
                  value={field.value}
                  onChange={(event) => field.onChange(event.target.value)}
                  countryCode={form.watch("contactPersonPhoneCountryCode")}
                  onCountryChange={(code) => form.setValue("contactPersonPhoneCountryCode", code)}
                  placeholder={t("placeholder-contact-person-phone")}
                  error={fieldState.error}
                />
              )}
            />

            {vendorType === "company" && (
              <Input
                label={t("label-legal-name")}
                required
                placeholder={t("placeholder-legal-name")}
                data-testid="input-legal-name"
                {...register("legalName")}
                error={errors.legalName}
              />
            )}

            <Controller
              name="status"
              control={form.control}
              render={({ field, fieldState }) => (
                <Select
                  label={t("label-status")}
                  required
                  data-testid="select-status"
                  options={[
                    { label: t("status-active"), value: "active" },
                    { label: t("status-inactive"), value: "inactive" },
                  ]}
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                  error={fieldState.error}
                />
              )}
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button type="button" variant="outline" color="tertiary" onClick={onNext}>
            {t("btn-next")}
          </Button>
        </div>
      </div>
    </VendorAccordion>
  );
};

export default VendorInformationAccordion;
